import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { InputAdornment, styled, TextField } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { toZonedTime } from 'date-fns-tz';
import { CalendarDayRange } from 'further-types/utils';
import { DateRangePicker as MuiDateRangePicker } from 'materialui-daterange-picker';
import { FC, useState } from 'react';
import { CalendarDay } from '../../utils';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& .date-range-picker-wrapper > [class^="materialui-daterange-picker-MuiPaper-root"]':
    {
      boxShadow: 'none',
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
}));

const ClearButton = styled(IconButton)({
  padding: 0,
});

const formatString = 'dd/MM/yyyy';

type Props = {
  dateRange?: CalendarDayRange;
  onDateRangeChange: (dateRange: CalendarDayRange | undefined) => void;
  placeholder?: string;
  definedRanges?: Array<{
    label: string;
    startDate: CalendarDay;
    endDate: CalendarDay;
  }>;
};

//TODO: Style modal when designs ready
const DateRangePicker: FC<Props> = ({
  dateRange,
  onDateRangeChange,
  placeholder,
  definedRanges,
}) => {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    setOpen(!open);
  };

  const getDisplayDateRange = (dateRange?: CalendarDayRange) => {
    if (!dateRange) {
      return '';
    }

    const startDate = dateRange.startDate.toLabel('--', formatString);
    const endDate = dateRange.endDate.toLabel('--', formatString);

    return `${startDate} - ${endDate}`;
  };

  return (
    <>
      <TextField
        size="small"
        placeholder={placeholder}
        value={getDisplayDateRange(dateRange)}
        onClick={toggle}
        fullWidth
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {!!getDisplayDateRange(dateRange) && (
                  <ClearButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDateRangeChange(undefined);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </ClearButton>
                )}
                <DateRangeIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <StyledModal
        open={open}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <div>
            <MuiDateRangePicker
              open={true}
              toggle={toggle}
              onChange={(range) => {
                if (!range.startDate || !range.endDate) {
                  return;
                }
                onDateRangeChange({
                  startDate: new CalendarDay(range.startDate),
                  endDate: new CalendarDay(range.endDate),
                });
                toggle();
              }}
              initialDateRange={{
                startDate: dateRange
                  ? toZonedTime(dateRange.startDate.startOfDay(), 'UTC')
                  : undefined,
                endDate: dateRange
                  ? toZonedTime(dateRange.endDate.endOfDay(), 'UTC')
                  : undefined,
              }}
              definedRanges={definedRanges?.map((range) => ({
                label: range.label,
                startDate: toZonedTime(range.startDate.startOfDay(), 'UTC'),
                endDate: toZonedTime(range.endDate.endOfDay(), 'UTC'),
              }))}
              wrapperClassName="date-range-picker-wrapper"
              minDate={new Date('1900-01-01')}
            />
          </div>
        </Fade>
      </StyledModal>
    </>
  );
};

export default DateRangePicker;
